import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Icons,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetLabReportsByPatientQueryKey,
  useCreateLabReportByPatient,
  useDeleteLabReport,
  useUpdateLabReport,
} from '../../../../../api/generated/diagnosis-controller/diagnosis-controller.ts';
import {
  CreateLabReportDto,
  LabReportDetectionMethodDto,
  LabReportDetectionMethodDtoMethod,
  LabReportDetectionMethodDtoTestResult,
  LabReportDto,
  LabReportDtoLabTestMaterial,
  LabReportDtoSpecies,
  LabReportMutationDto,
  LabReportMutationDtoEnzyme,
  LabReportMutationDtoMutationStatus,
  LabReportResistanceDto,
  LabReportResistanceDtoResistance,
  LabReportResistanceDtoResistanceName,
} from '../../../../../api/generated/model';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getDefaultValueForDateInput } from '../../../../../utils/date.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';
import {
  getDetectionMethodName,
  getEnzymeName,
  getMutationStatusName,
  getResistanceName,
  getResistanceNameName,
  getSpeciesName,
  getTestResultName,
  getTestTypeName,
} from '../../../../../utils/labReport.tsx';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { DeleteConfirmationDialog } from '../../../../components/DeleteConfirmationDialog.tsx';

interface DetectionMethodFormValues {
  method: LabReportDetectionMethodDtoMethod;
  testResult: LabReportDetectionMethodDtoTestResult | null;
}

interface ResistanceFormValues {
  resistanceName: LabReportResistanceDtoResistanceName;
  resistance: LabReportResistanceDtoResistance | null;
}

interface MutationFormValues {
  enzyme: LabReportMutationDtoEnzyme;
  mutationStatus: LabReportMutationDtoMutationStatus | null;
}

interface FormValues {
  samplingDate: string;
  labTestMaterial: LabReportDtoLabTestMaterial | null;
  otherMaterial: string;
  detectionMethods: DetectionMethodFormValues[];
  species: LabReportDtoSpecies | null;
  file: File | null;
  resistances: ResistanceFormValues[];
  mutations: MutationFormValues[];
  notes: string;
}

export function CreateLabReportDialog({
  open,
  onClose,
  patientId,
  currentLabReport,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  currentLabReport?: LabReportDto;
}) {
  const queryClient = useQueryClient();

  const isEditing = !!currentLabReport?.id;
  const [isDeleteLabReportConfirmationDialogOpen, setIsDeleteLabReportConfirmationDialogOpen] = useState(false);

  const {
    register,
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(currentLabReport),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { fields: detectionMethodFields, update: updateDetectionMethod } = useFieldArray({
    control,
    name: 'detectionMethods',
  });
  const { fields: resistanceFields, update: updateResistance } = useFieldArray({ control, name: 'resistances' });

  const { fields: mutationFields, update: updateMutation } = useFieldArray({
    control,
    name: 'mutations',
  });

  const handleOnClose = (reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    reset();
    onClose();
  };

  const createLabReportMutation = useCreateLabReportByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabReportsByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim erstellen des MLB ist ein Fehler aufgetreten'),
    },
  });

  const updateLabReportMutation = useUpdateLabReport({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabReportsByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim updated des MLB ist ein Fehler aufgetreten'),
    },
  });

  const deleteLabReportMutation = useDeleteLabReport({
    mutation: {
      onSuccess: () => {
        toast.success('MLB wurde erfolgreich gelöscht');
        setIsDeleteLabReportConfirmationDialogOpen(false);
        void queryClient.invalidateQueries({ queryKey: getGetLabReportsByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Löschen der MLB ist ein Fehler aufgetreten'),
    },
  });

  const labTestMaterial = watch('labTestMaterial');
  const getOtherMaterialEnabled = (testType: LabReportDtoLabTestMaterial | null) =>
    (
      [
        LabReportDtoLabTestMaterial.OTHER_MATERIAL,
        LabReportDtoLabTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS,
      ] as (LabReportDtoLabTestMaterial | null)[]
    ).includes(testType);

  const otherMaterialEnabled = getOtherMaterialEnabled(labTestMaterial);

  const handleOnSubmit = async (data: FormValues) => {
    const createLabReportDto: CreateLabReportDto = {
      file: data.file
        ? {
            fileName: data.file.name,
            file: await blobToBase64(data.file),
          }
        : undefined,
      labTestMaterial: data.labTestMaterial!,
      otherMaterial: data.otherMaterial,
      samplingDate: data.samplingDate,
      species: data.species,
      resistances: data.resistances.filter(({ resistance }) => resistance) as LabReportResistanceDto[],
      mutations: data.mutations.filter(({ mutationStatus }) => mutationStatus) as LabReportMutationDto[],
      detectionMethods: data.detectionMethods.filter(({ testResult }) => testResult) as LabReportDetectionMethodDto[],
      notes: getStringOrUndefined(data.notes),
    };

    if (isEditing) {
      updateLabReportMutation.mutate({ labReportId: currentLabReport.id, data: createLabReportDto });
    } else {
      createLabReportMutation.mutate({ patientId: patientId, data: createLabReportDto });
    }
  };

  useEffect(() => {
    if (!otherMaterialEnabled) {
      setValue('otherMaterial', '');
      clearErrors('otherMaterial');
    }
  }, [otherMaterialEnabled]);

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(currentLabReport));
    }
  }, [open, currentLabReport]);

  return (
    <>
      <Modal open={open} onClose={(_, reason) => handleOnClose(reason)}>
        <ModalDialog sx={{ width: 850, overflowY: 'scroll' }}>
          <ModalClose />
          <Typography level={'h4'}>{isEditing ? 'MLB bearbeiten' : 'Neue MLB'}</Typography>
          <Divider />
          <form onSubmit={handleSubmit(handleOnSubmit)}>
            <Stack gap={2}>
              <FormGrid>
                <FormControl error={!!errors.labTestMaterial}>
                  <FormLabel>
                    <Typography>Test-Typ *</Typography>
                  </FormLabel>
                  <Controller
                    name="labTestMaterial"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={(_, value) => onChange(value)}>
                        {Object.values(LabReportDtoLabTestMaterial).map((testMaterial) => (
                          <Option value={testMaterial} key={testMaterial}>
                            {getTestTypeName(testMaterial)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.labTestMaterial && <FormHelperText>Test-Typ muss ausgefüllt werden</FormHelperText>}
                </FormControl>
                <FormControl error={!!errors.samplingDate}>
                  <FormLabel>
                    <Typography>Meldedatum</Typography>
                  </FormLabel>
                  <GermanDateInput
                    control={control}
                    name="samplingDate"
                    rules={
                      {
                        // required: true,
                      }
                    }
                  />
                  {errors.samplingDate && <FormHelperText>Meldedatum muss ausgefüllt werden</FormHelperText>}
                </FormControl>
                {getOtherMaterialEnabled(labTestMaterial) && (
                  <>
                    <FormControl error={!!errors.otherMaterial}>
                      <FormLabel>
                        <Typography>Material *</Typography>
                      </FormLabel>
                      <Input
                        {...register('otherMaterial', {
                          required: getOtherMaterialEnabled(labTestMaterial),
                        })}
                      />

                      {errors.otherMaterial && <FormHelperText>Material muss ausgefüllt werden</FormHelperText>}
                    </FormControl>
                    <div />
                  </>
                )}
                <FormControl error={!!errors.file}>
                  <FormLabel>
                    <Typography>MLB PDF *</Typography>
                  </FormLabel>
                  <Controller
                    name="file"
                    control={control}
                    rules={{ required: !isEditing }}
                    render={({ field: { onChange, value } }) => (
                      <FileInput
                        value={value}
                        onChange={onChange}
                        accept="application/pdf"
                        placeholder={currentLabReport?.fileDetails?.fileName ?? 'MLB PDF'}
                      />
                    )}
                  />
                  {errors.file && <FormHelperText>MLB PDF muss hochgeladen werden</FormHelperText>}
                </FormControl>
              </FormGrid>
              <Divider inset={'context'} />
              <Stack gap={1.5}>
                <Typography level={'title-lg'}>Nachweismethode</Typography>
                {detectionMethodFields.map((detectionMethodField, index) => (
                  <Fragment key={detectionMethodField.id}>
                    <Stack direction={'row'} justifyContent={'space-between'}>
                      <FormLabel>{getDetectionMethodName(detectionMethodField.method)}</FormLabel>
                      <ToggleButtonGroup value={detectionMethodField.testResult}>
                        {Object.values(LabReportDetectionMethodDtoTestResult).map((result) => (
                          <Button
                            value={result}
                            key={result}
                            onClick={() =>
                              updateDetectionMethod(index, {
                                method: detectionMethodField.method,
                                testResult: result,
                              })
                            }>
                            {getTestResultName(result)}
                          </Button>
                        ))}
                        <IconButton
                          disabled={!detectionMethodField.testResult}
                          onClick={() =>
                            updateDetectionMethod(index, {
                              method: detectionMethodField.method,
                              testResult: null,
                            })
                          }>
                          <Icons.HighlightOff />
                        </IconButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Fragment>
                ))}
              </Stack>

              <Divider inset="context" />

              <FormControl sx={{ gap: 1 }}>
                <FormLabel>
                  <Typography level="title-lg">Spezies</Typography>
                </FormLabel>
                <Controller
                  name={'species'}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value}
                      onChange={(_, value) => onChange(value)}
                      indicator={!value ? <Icons.UnfoldMore /> : null}
                      endDecorator={
                        value && (
                          <IconButton onClick={() => onChange(null)}>
                            <Icons.Close />
                          </IconButton>
                        )
                      }>
                      {Object.values(LabReportDtoSpecies).map((species) => (
                        <Option value={species} key={species}>
                          {getSpeciesName(species)}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <Divider inset="context" />
              <Stack gap={1.5}>
                <Typography level="title-lg">Resistenz</Typography>
                {resistanceFields.map((resistanceField, index) => (
                  <Fragment key={resistanceField.id}>
                    <Stack direction={'row'}>
                      <FormLabel sx={{ flex: 1 }}>{getResistanceNameName(resistanceField.resistanceName)}</FormLabel>
                      <ToggleButtonGroup sx={{ flex: 5 }} value={resistanceField.resistance}>
                        {Object.values(LabReportResistanceDtoResistance).map((resistance) => (
                          <Button
                            value={resistance}
                            key={resistance}
                            onClick={() =>
                              updateResistance(index, {
                                resistanceName: resistanceField.resistanceName,
                                resistance: resistance,
                              })
                            }>
                            {getResistanceName(resistance)}
                          </Button>
                        ))}
                        <IconButton
                          disabled={!resistanceField.resistance}
                          onClick={() =>
                            updateResistance(index, {
                              resistanceName: resistanceField.resistanceName,
                              resistance: null,
                            })
                          }>
                          <Icons.HighlightOff />
                        </IconButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Fragment>
                ))}
              </Stack>
              <Divider inset="context" />
              <Stack gap={1.5}>
                <Typography level="title-lg">Mutation</Typography>
                {mutationFields.map((mutationField, index) => (
                  <Fragment key={mutationField.id}>
                    <Stack direction={'row'}>
                      <FormLabel sx={{ flex: 1 }}>{getEnzymeName(mutationField.enzyme)}</FormLabel>
                      <ToggleButtonGroup sx={{ flex: 5 }} value={mutationField.mutationStatus}>
                        {Object.values(LabReportMutationDtoMutationStatus).map((mutationStatus) => (
                          <Button
                            value={mutationStatus}
                            key={mutationStatus}
                            onClick={() => updateMutation(index, { enzyme: mutationField.enzyme, mutationStatus })}>
                            {getMutationStatusName(mutationStatus)}
                          </Button>
                        ))}
                        <IconButton
                          disabled={!mutationField.mutationStatus}
                          onClick={() => updateMutation(index, { enzyme: mutationField.enzyme, mutationStatus: null })}>
                          <Icons.HighlightOff />
                        </IconButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Fragment>
                ))}
              </Stack>
              <Divider inset="context" />
              <FormControl>
                <FormLabel>
                  <Typography>Bemerkungen</Typography>
                </FormLabel>
                <Textarea {...register('notes')} minRows={2} maxRows={4} />
              </FormControl>

              <FormActionButtons
                onAbortClick={handleOnClose}
                disableSubmit={!isFormDirty}
                isLoading={createLabReportMutation.isPending}>
                {currentLabReport && (
                  <Button
                    color={'danger'}
                    endDecorator={<Icons.Delete />}
                    loading={deleteLabReportMutation.isPending}
                    onClick={() => setIsDeleteLabReportConfirmationDialogOpen(true)}>
                    Löschen
                  </Button>
                )}
              </FormActionButtons>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
      {currentLabReport && (
        <DeleteConfirmationDialog
          open={isDeleteLabReportConfirmationDialogOpen}
          onCancel={() => setIsDeleteLabReportConfirmationDialogOpen(false)}
          onDelete={() => {
            deleteLabReportMutation.mutate({ id: currentLabReport.id });
          }}
          modalTitle={'MLB löschen'}>
          <Typography>Ausgewähltes MLB wirklich löschen?</Typography>
        </DeleteConfirmationDialog>
      )}
    </>
  );
}

function getDefaultValues(labReport?: LabReportDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(labReport?.samplingDate),
    labTestMaterial: labReport?.labTestMaterial ?? null,
    otherMaterial: labReport?.otherMaterial ?? '',
    detectionMethods: [
      {
        method: LabReportDetectionMethodDtoMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION,
        testResult:
          findDetectionMethodTestResult(
            LabReportDetectionMethodDtoMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION,
            labReport,
          ) ?? null,
      },
      {
        method: LabReportDetectionMethodDtoMethod.MICROSCOPIC_DIRECT_DETECTION,
        testResult:
          findDetectionMethodTestResult(LabReportDetectionMethodDtoMethod.MICROSCOPIC_DIRECT_DETECTION, labReport) ??
          null,
      },
      {
        method: LabReportDetectionMethodDtoMethod.CULTURE,
        testResult: findDetectionMethodTestResult(LabReportDetectionMethodDtoMethod.CULTURE, labReport) ?? null,
      },
    ],
    species: labReport?.species ?? null,
    file: null,
    resistances: [
      {
        resistanceName: LabReportResistanceDtoResistanceName.ETHAMBUTOL,
        resistance: findResistance(LabReportResistanceDtoResistanceName.ETHAMBUTOL, labReport) ?? null,
      },
      {
        resistanceName: LabReportResistanceDtoResistanceName.ISONIAZID,
        resistance: findResistance(LabReportResistanceDtoResistanceName.ISONIAZID, labReport) ?? null,
      },
      {
        resistanceName: LabReportResistanceDtoResistanceName.PYRAZINAMID,
        resistance: findResistance(LabReportResistanceDtoResistanceName.PYRAZINAMID, labReport) ?? null,
      },
      {
        resistanceName: LabReportResistanceDtoResistanceName.RIFAMPICIN,
        resistance: findResistance(LabReportResistanceDtoResistanceName.RIFAMPICIN, labReport) ?? null,
      },
    ],
    mutations: [
      {
        enzyme: LabReportMutationDtoEnzyme.RPOB,
        mutationStatus: findMutationStatus(LabReportMutationDtoEnzyme.RPOB, labReport) ?? null,
      },
      {
        enzyme: LabReportMutationDtoEnzyme.KATG,
        mutationStatus: findMutationStatus(LabReportMutationDtoEnzyme.KATG, labReport) ?? null,
      },
      {
        enzyme: LabReportMutationDtoEnzyme.INHA_PROMOTER,
        mutationStatus: findMutationStatus(LabReportMutationDtoEnzyme.INHA_PROMOTER, labReport) ?? null,
      },
    ],
    notes: labReport?.notes ?? '',
  };
}

function findDetectionMethodTestResult(
  method: LabReportDetectionMethodDtoMethod,
  labReport?: LabReportDto,
): LabReportDetectionMethodDtoTestResult | undefined {
  return labReport?.detectionMethods.find((methodDto) => methodDto.method === method)?.testResult;
}

function findResistance(
  name: LabReportResistanceDtoResistanceName,
  labReport?: LabReportDto,
): LabReportResistanceDtoResistance | undefined {
  return labReport?.resistances.find((resistance) => resistance.resistanceName === name)?.resistance;
}

function findMutationStatus(
  enzyme: LabReportMutationDtoEnzyme,
  labReport?: LabReportDto,
): LabReportMutationDtoMutationStatus | undefined {
  return labReport?.mutations.find((mutation) => mutation.enzyme === enzyme)?.mutationStatus;
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
